<template>
  <div
    :class="['card-item', { 'card-item--compact': subtitle || warningText, 'card-item--with-warning': !!warningText }]"
    :disabled="inactive || undefined"
    @click="handleClick"
  >
    <span v-if="logoSrc" class="card-item__logo">
      <img :src="logoSrc" :alt="logoAlt" :style="{ maxWidth: logoSize, maxHeight: logoSize }" />
    </span>
    <span class="card-item__content">
      <span class="bold">{{ title }}</span>
      <span v-if="subtitle" class="card-item__subtitle">{{ subtitle }}</span>
      <span v-if="warningText" class="payment-provider-limit-message">{{ warningText }}</span>
    </span>
    <span v-if="rightText" :class="['card-item__right', { bold: rightTextBold }]">{{ rightText }}</span>
    <img v-if="rightIcon" class="card-item__chevron" :src="typeof rightIcon === 'string' ? rightIcon : undefined" :alt="rightIconAlt" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import chevronRightIcon from "@/assets/icons/chevron_right.svg";

export default defineComponent({
  name: "CardItem",
  props: {
    logoSrc: { type: String, default: "" },
    logoAlt: { type: String, default: "" },
    logoSize: { type: String, default: "32px" },
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    rightText: { type: String, default: "" },
    rightTextBold: { type: Boolean, default: false },
    rightIcon: { type: String, default: () => chevronRightIcon },
    rightIconAlt: { type: String, default: "Chevron Right" },
    // Inline warning rendered inside the card, below the title. Used to surface
    // the provider amount-limit message without breaking the card layout.
    warningText: { type: String, default: "" },
    // Purely cosmetic: dims the card to signal an inactive state. It does NOT
    // block the click — the parent owns the decision of what happens on click.
    inactive: { type: Boolean, default: false },
  },
  emits: ["click"],
  setup(_props, { emit }) {
    const handleClick = () => {
      emit("click");
    };
    return { handleClick };
  },
});
</script>

<style scoped>
.card-item {
  display: flex;
  padding: 24px 16px;
  margin-top: 16px;
  align-items: center;
  border-radius: 16px;
  background: white;
  box-shadow: 0 4px 8px 3px rgba(169, 169, 169, 0.15);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.card-item:hover:not([disabled]) {
  background-color: #f5f5f5;
}

.card-item__logo {
  text-align: left;
  align-items: center;
  display: flex;
  min-width: 40px;
}

.card-item__logo img {
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
}

span.bold {
  line-height: 26px;
}

.card-item__content {
  margin-inline: 12px;
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  text-align: left;
  color: black;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
}

.card-item__subtitle {
  color: var(--Typography-Text-2);
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
}

.card-item__right {
  margin-left: auto;
}

.card-item__chevron {
  margin-left: 8px;
}

.card-item--compact {
  padding: 16px;
}

.card-item--with-warning {
  padding: 14px 16px;
}

.payment-provider-limit-message {
  color: #c9372c;
  text-align: left;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  margin-top: 0;
}

.card-item[disabled] {
  cursor: default;
  opacity: 0.5;
}

.card-item[disabled] .card-item__logo img {
  filter: grayscale(100%);
}
</style>
